// =============================================
// Post-Content Audit — QA gate po humanization
// Sprawdza jakość artykułu przed zatwierdzeniem do publikacji
// =============================================

import type { NodeContentContext, PostContentAuditResult, QaStatus } from "@/types/thematicMaps";

// Strip PL diacritics for matching
const PL_MAP: Record<string, string> = {
  ą: "a", ć: "c", ę: "e", ł: "l", ń: "n", ó: "o", ś: "s", ź: "z", ż: "z",
};
function normalize(text: string): string {
  return text.toLowerCase().replace(/[ąćęłńóśźż]/g, (ch) => PL_MAP[ch] || ch).trim();
}

/**
 * Run post-content audit on final HTML.
 * Returns quality score + qa_status + detailed issues.
 */
export function postContentAudit(
  finalHtml: string | null | undefined,
  ctx: NodeContentContext,
  engineDebug: Record<string, unknown>,
): PostContentAuditResult {
  const issues: PostContentAuditResult["issues"] = [];

  // 1. Final HTML exists?
  if (!finalHtml || finalHtml.trim().length < 100) {
    return {
      quality_score: 0,
      qa_status: "failed",
      has_sources: false,
      has_internal_links: false,
      passes_boundary_check: true,
      required_entities_coverage: 0,
      issues: [{ code: "NO_CONTENT", severity: "critical", message: "Brak treści (final_html puste lub za krótkie)" }],
    };
  }

  const htmlLower = finalHtml.toLowerCase();
  const htmlNormalized = normalize(finalHtml);

  // 2. Sources check — wykrywa AKTUALNY system cytowan (stopka id="zrodla"
  // + przypisy <sup>[n]</sup> href="#zrodlo-n"); stare formaty "(Źródło:"
  // zostaja jako fallback dla archiwalnych artykulow. Do 2026-08-23 czujnik
  // znal TYLKO stary format — bonus +15 nie dzialal dla ZADNEGO wspolczesnego
  // artykulu ("sources: BRAK" w kazdym logu QA mimo kompletu przypisow).
  const has_sources =
    htmlLower.includes('id="zrodla"') ||
    htmlLower.includes('href="#zrodlo-') ||
    htmlLower.includes("(źródło:") ||
    htmlLower.includes("(source:");

  // 3. H2 structure
  const h2Count = (finalHtml.match(/<h2[^>]*>/gi) || []).length;
  if (h2Count < 2) {
    issues.push({ code: "LOW_H2", severity: "warning", message: `Tylko ${h2Count} sekcji H2 (minimum 2)` });
  }

  // 4. Required entities coverage — normalized + aliases
  let required_entities_coverage = 1.0;
  const requiredEntities = ctx.node.required_entities || [];

  if (requiredEntities.length > 0) {
    // Build normalized search terms (entity names + aliases)
    const entitySearchTerms: Array<{ entity: string; terms: string[] }> = [];
    for (const reqEntity of requiredEntities) {
      const terms = [normalize(reqEntity)];
      // Add aliases from knowledge entities
      const knowledgeEntity = ctx.knowledge.entities.find(
        (e) => normalize(e.canonical_name) === normalize(reqEntity),
      );
      if (knowledgeEntity) {
        for (const alias of knowledgeEntity.aliases || []) {
          terms.push(normalize(alias));
        }
      }
      entitySearchTerms.push({ entity: reqEntity, terms });
    }

    let matched = 0;
    for (const { entity, terms } of entitySearchTerms) {
      // Try 1: exact substring match (covers single-word entities + multi-word
      // gdy są w treści jako jeden ciąg, np. "growth hacking")
      const foundExact = terms.some((term) => htmlNormalized.includes(term));

      // Try 2: token-based fallback dla multi-word entities (≥2 tokens, ≥3 chars
      // each) — accept gdy ≥50% tokenów obecnych jako osobne słowa.
      // Przykład: "ICE RICE PIE" (taxonomic group) — artykuł zwykle mówi o
      // konkretnych frameworkach osobno ("framework ICE", "porównanie z RICE i
      // PIE"), nie jako jeden ciąg "ICE RICE PIE".
      let foundTokens = false;
      if (!foundExact) {
        for (const term of terms) {
          const tokens = term.split(/\s+/).filter((t) => t.length >= 3);
          if (tokens.length >= 2) {
            const tokenMatched = tokens.filter((t) => htmlNormalized.includes(t)).length;
            if (tokenMatched / tokens.length >= 0.5) {
              foundTokens = true;
              break;
            }
          }
        }
      }

      if (foundExact || foundTokens) {
        matched++;
      } else {
        issues.push({ code: "MISSING_ENTITY", severity: "warning", message: `Brak wymaganej encji: "${entity}"` });
      }
    }
    required_entities_coverage = matched / requiredEntities.length;
  }
  // Pusta lista required_entities → coverage = 1.0 (nie karz score)

  // 5. Internal links check — graduated scoring
  const linkMatches = finalHtml.match(/<a[^>]*href="\/[^"]*"[^>]*>/g) || [];
  const uniqueInternalLinksCount = new Set(
    linkMatches.map(m => m.match(/href="(\/[^"]*)"/)?.[1]).filter(Boolean)
  ).size;
  const has_internal_links = uniqueInternalLinksCount > 0;

  // 6. Boundary check — NOT primitive
  let passes_boundary_check = true;
  const boundariesNotOwned = ctx.map.boundaries_not_owned || [];

  for (const boundary of boundariesNotOwned) {
    const boundaryNorm = normalize(boundary);
    if (boundaryNorm.length < 3) continue;

    // Count occurrences
    let count = 0;
    let searchPos = 0;
    while (true) {
      const idx = htmlNormalized.indexOf(boundaryNorm, searchPos);
      if (idx === -1) break;
      count++;
      searchPos = idx + boundaryNorm.length;
    }

    if (count === 0) continue;

    // Check if boundary topic has its own H2/H3 section
    const headingRegex = new RegExp(`<h[23][^>]*>[^<]*${boundaryNorm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}[^<]*<\\/h[23]>`, "i");
    const hasOwnSection = headingRegex.test(normalize(finalHtml));

    if (hasOwnSection || count > 5) {
      passes_boundary_check = false;
      issues.push({
        code: "BOUNDARY_VIOLATION",
        severity: "critical",
        message: `Temat "${boundary}" (z not_owned) ma ${hasOwnSection ? "osobną sekcję" : `${count} wystąpień`} — przekroczenie granic mapy`,
      });
    } else if (count >= 2) {
      issues.push({
        code: "BOUNDARY_MENTION",
        severity: "warning",
        message: `Temat "${boundary}" (z not_owned) wspomniany ${count}× — kontekstowe, ale monitoruj`,
      });
    }
  }

  // 7. Optimization warnings from engineDebug
  // sources_preserved=false to flaga Z POLOWY procesu — bezpiecznik odzysku
  // zrodel czesto odbudowuje przypisy PO niej. Kara -20 za wyleczona awarie
  // robila z QA rzut moneta: identyczna tresc raz 100/approved, raz
  // 85/needs_review (incydent "zaproszenia" 2026-08-23). Krytyk zostaje tylko,
  // gdy FINAL naprawde nie ma zrodel.
  const optDebug = engineDebug?.optimization as Record<string, unknown> | undefined;
  if (optDebug && (optDebug.sources_preserved === false)) {
    if (has_sources) {
      issues.push({
        code: "SOURCES_RECOVERED",
        severity: "warning",
        message: "Źródła chwilowo zgubione przy optymalizacji — odzyskane, finał kompletny",
      });
    } else {
      issues.push({
        code: "SOURCES_LOST",
        severity: "critical",
        message: "Źródła zagubione podczas optymalizacji i NIEODZYSKANE (brak w finale)",
      });
    }
  }

  // 7b. Diagnostic — sanity check enrichment → final pipeline (regression detector).
  // Jeśli enrichment wstawił sources ale w final_html ich nie ma → coś je zgubiło
  // po drodze (humanization? jakiś nieoczekiwany filter?). Warning, nie blocker.
  const enrichDebug = engineDebug?.enrichment as Record<string, unknown> | undefined;
  const enrichedSources = typeof enrichDebug?.sources_inserted === "number"
    ? enrichDebug.sources_inserted
    : 0;
  if (enrichedSources > 0 && !has_sources) {
    issues.push({
      code: "SOURCES_DROPPED_PIPELINE",
      severity: "warning",
      message: `Enrichment wstawił ${enrichedSources} źródeł, ale final_html ich nie ma — sprawdź optimization/humanization regex stripping.`,
    });
  }
  // Odwrotny kierunek: stopka Zrodla obecna, choc system NIC nie wstawil —
  // model ja ZMYSLIL (incydent 2026-08-23: oba URL-e 404). Straznik
  // stripFabricatedSources powinien to wycinac PRZED QA; ten krytyk to pas
  // bezpieczenstwa, gdyby cokolwiek sie przeslizgnelo.
  // (tylko gdy debug enrichmentu ISTNIEJE i jawnie mowi 0 — brak debugu
  // nie moze byc dowodem fabrykacji)
  if (typeof enrichDebug?.sources_inserted === "number" && enrichDebug.sources_inserted === 0 && htmlLower.includes('id="zrodla"')) {
    issues.push({
      code: "SOURCES_FABRICATED",
      severity: "critical",
      message: "Stopka Źródła obecna, choć enrichment nie wstawił żadnych źródeł — linki zmyślone przez model.",
    });
  }
  if (optDebug && Array.isArray(optDebug.warnings) && optDebug.warnings.length > 0) {
    for (const warn of optDebug.warnings as string[]) {
      issues.push({ code: "OPT_WARNING", severity: "warning", message: warn });
    }
  }
  // Warningi humanizacji (2026-08-24): humanizacja gubila linki wewnetrzne
  // (Slownik: 3->1) i nie zostawialo to ZADNEGO sladu w QA — teraz kazda strata
  // i kazdy odzysk z restoreLinks laduje w raporcie.
  const humanDebug = engineDebug?.humanization as Record<string, unknown> | undefined;
  if (humanDebug && Array.isArray(humanDebug.warnings) && humanDebug.warnings.length > 0) {
    for (const warn of humanDebug.warnings as string[]) {
      issues.push({ code: "HUMAN_WARNING", severity: "warning", message: warn });
    }
  }

  // --- SCORING ---
  let score = 60;
  if (has_sources) score += 15;
  score += uniqueInternalLinksCount >= 2 ? 10 : uniqueInternalLinksCount === 1 ? 5 : 0;
  if (passes_boundary_check) score += 10;
  // +5 per 20% coverage, max +25
  score += Math.min(25, Math.floor(required_entities_coverage * 5) * 5);
  // -20 per critical
  const criticalCount = issues.filter((i) => i.severity === "critical").length;
  score -= criticalCount * 20;
  // Clamp
  score = Math.max(0, Math.min(100, score));

  // --- QA STATUS ---
  let qa_status: QaStatus;
  if (score < 40) {
    qa_status = "failed";
  } else if (criticalCount > 0) {
    qa_status = "needs_review";
  } else if (score >= 70) {
    qa_status = "approved";
  } else {
    qa_status = "needs_review";
  }

  return {
    quality_score: score,
    qa_status,
    has_sources,
    has_internal_links,
    passes_boundary_check,
    required_entities_coverage,
    issues,
  };
}
